// this popup is used on home page

import React, { useState } from "react";
import { View, Text, TouchableOpacity, Modal, Image } from "react-native";
import {
  CreateResponsiveStyle,
  DEVICE_SIZES,
  useDeviceSize,
} from "rn-responsive-styles";
import { LinearGradient } from "expo-linear-gradient";
import { AppColors } from "../../utility/AppColors";
import { AppFontStyle } from "../../styles/AppFontStyle";
import { AppImages } from "../../utility/AppImages";
import { redeemBalance } from "../../services/authAPI";
import { toast } from "react-toastify";
import { useAuthProvider } from "../../context/AuthContext";
import { ShowLoader } from "../../common/ShowLoader";

export default function CashOutDetailsPopup({
  visible,
  onClose,
  accessToken,
  totalAmount,
  setIsCashOutModalVisible,
  userProfileData,
}) {
  const deviceSize = useDeviceSize();
  const styles = useStyles();
  const [showLoader, setShowLoader] = useState(false);
  const { isActiveSubscriptionFound } = useAuthProvider();
  const redeemAccountBalance = async () => {
    setShowLoader(true);
    try {
      const data = await redeemBalance(accessToken, totalAmount);

      if (data.statusCode === 200) {
        const delay = (ms) => new Promise((resolve) => setTimeout(resolve, ms));
        await delay(3000);
        onClose();
        setShowLoader(false);
        toast.success(data.body.message);
      } else if (data.statusCode === 500) {
        if (data.body.error.includes("You have insufficient")) {
          onClose();
          setShowLoader(false);
          toast.error("Please try again after some time");
        } else {
          // Handle other 500 errors
          onClose();
          setShowLoader(false);
          toast.error(data.body.error || "An internal server error occurred");
        }
      } else {
        onClose();
        setShowLoader(false);
        toast.error(
          data.body.error || "An error occurred during creating account"
        );
      }
    } catch (error) {
      onClose();
      setShowLoader(false);
      toast.error(error.message);
    }
  };
  const parsedAmount = parseFloat(totalAmount);
  const creditToPoundRate = 0.01; // 1 credit = £0.01
  const creditsRedeemed = Math.round(parsedAmount / creditToPoundRate);
  const feePercentage = isActiveSubscriptionFound ? 0.05 : 0.2; // 5% or 20%
  const platformFee = parsedAmount * feePercentage; // Fee in pounds
  const stripeFee = 2.00; // Fixed Stripe fee
  const finalAmountInPounds = parsedAmount - platformFee - stripeFee; // Final amount after fees
  const payoutTime = isActiveSubscriptionFound ? "6 hours" : "48 hours";
  const availableCredits = userProfileData?.credits || 0;
  const remainingBalance = availableCredits - creditsRedeemed; // Remaining credits after cashout
  return (
    <Modal transparent visible={visible} animationType="fade">
      <View style={styles.overlay}>
        <LinearGradient
          colors={["#111827", "#000"]}
          start={{ x: 0, y: 0 }}
          end={{ x: 1, y: 1 }}
          style={[styles.modalContainer]}
        >
          {/* Close Button */}
          <TouchableOpacity
            onPress={() => setIsCashOutModalVisible(false)}
            style={styles.closeButton}
          >
            <Image
              source={AppImages.icCancel}
              style={styles.closeIcon}
              resizeMode="contain"
            />
          </TouchableOpacity>

          {/* Header */}
          <Text style={styles.headerText}>Confirm payout</Text>

          {/* Cash-Out Details */}
          <LinearGradient
            colors={["rgba(31, 41, 55, 0.50)", "rgba(17, 24, 39, 0.50)"]}
            start={{ x: 0, y: 0 }}
            end={{ x: 1, y: 1 }}
            style={styles.detailsContainer}
          >
            {/* Credits Redeemed */}
            <View style={styles.detailRow}>
              <Text style={styles.labelText}>Credits Redeemed</Text>
              <Text style={styles.valueText}>{creditsRedeemed.toLocaleString()} KO Credits</Text>
            </View>

            {/* Equivalent Value */}
            <View style={styles.detailRow}>
              <Text style={styles.labelText}>Equivalent Value</Text>
              <Text style={styles.valueText}>£{parsedAmount.toFixed(2)}</Text>
            </View>

            {/* Platform Fee */}
            <View style={styles.detailRow}>
              <Text style={styles.labelText}>Platform Fee ({feePercentage * 100}%)</Text>
              <Text style={[styles.valueText, { color: AppColors.red_DC2626 }]}>
                -£{platformFee.toFixed(2)}
              </Text>
            </View>

            {/* Stripe Fee */}
            <View style={styles.detailRow}>
              <Text style={styles.labelText}>Stripe fee</Text>
              <Text style={[styles.valueText, { color: AppColors.red_DC2626 }]}>
                -£{stripeFee.toFixed(2)}
              </Text>
            </View>

            {/* Separator Line */}
            <View style={styles.separator} />

            {/* You'll Receive */}
            <View style={styles.detailRow}>
              <Text style={styles.labelText}>You'll Receive</Text>
              <Text style={[styles.valueText, { color: AppColors.red_DC2626 }]}>
                £{finalAmountInPounds.toFixed(2)}
              </Text>
            </View>

            {/* Payout Time */}
            <View style={styles.detailRow}>
              <Text style={styles.labelText}>Payout Time</Text>
              <Text style={styles.valueText}>{payoutTime}</Text>
            </View>

            {/* Remaining Balance */}
            <View style={styles.detailRow}>
              <Text style={styles.labelText}>Remaining Balance</Text>
              <Text style={styles.valueText}>{remainingBalance} KO Credits</Text>
            </View>
          </LinearGradient>

          {/* Action Buttons */}
          <View style={styles.buttonContainer}>
            <TouchableOpacity
              onPress={() => setIsCashOutModalVisible(false)}
              style={styles.makeChangesButton}
            >
              <Text style={styles.makeChangesText}>Make changes</Text>
            </TouchableOpacity>

            <LinearGradient
              colors={["#DC2626", "#991B1B"]}
              start={{ x: 0, y: 0 }}
              end={{ x: 1, y: 0 }}
              style={styles.confirmButton}
            >
              <TouchableOpacity
                onPress={() => redeemAccountBalance()}
                style={styles.confirmButtonInner}
              >
                <Text style={styles.confirmText}>Confirm & cash out</Text>
              </TouchableOpacity>
            </LinearGradient>
          </View>
        </LinearGradient>
      </View>
      {<ShowLoader showIndicater={showLoader} isBackGround={true} />}
    </Modal>
  );
}

/* Internal Styles */
const useStyles = CreateResponsiveStyle(
  {
    overlay: {
      flex: 1,
      backgroundColor: "rgba(0, 0, 0, 0.66)",
      justifyContent: "center",
      alignItems: "center",
    },
    modalContainer: {
      display: "flex",
      borderRadius: 8,
      borderWidth: 1.6,
      borderColor: "rgba(239, 68, 68, 0.50)",
      padding: 24,
      flexDirection: "column",
      justifyContent: "center",
      alignItems: "center",
      gap: 22,
      width: "60%",
      maxWidth: 650,
    },
    closeButton: {
      position: "absolute",
      right: 10,
      top: 10,
      padding: 5,
      zIndex: 10,
    },
    closeIcon: {
      height: 15,
      width: 15,
    },
    headerText: {
      ...AppFontStyle.BOLD_ANYBODY_24,
      color: AppColors.white,
      alignSelf: "flex-start",
    },
    detailsContainer: {
      display: "flex",
      height: 290,
      padding: 12,
      flexDirection: "column",
      justifyContent: "center",
      alignItems: "center",
      gap: 13.793,
      alignSelf: "stretch",
      borderRadius: 8,
      borderWidth: 1,
      borderColor: "#374151",
    },
    detailRow: {
      flexDirection: "row",
      justifyContent: "space-between",
      alignItems: "center",
      width: "100%",
    },
    separator: {
      height: 1,
      backgroundColor: "#374151",
      alignSelf: "stretch",
      width: "100%",
    },
    labelText: {
      ...AppFontStyle.REGULAR_ANYBODY_14,
      color: AppColors.gray_9CA3AF,
    },
    valueText: {
      ...AppFontStyle.SEMIBOLD_ANYBODY_16,
      color: AppColors.white,
    },
    buttonContainer: {
      flexDirection: "row",
      gap: 12,
      alignSelf: "stretch",
      width: "100%",
      justifyContent: "space-between",
    },
    makeChangesButton: {
      paddingVertical: 12,
      paddingHorizontal: 24,
      borderRadius: 6,
      alignItems: "center",
      justifyContent: "center",
      backgroundColor: "transparent",
      borderWidth: 1.6,
      borderColor: AppColors.white,
    },
    makeChangesText: {
      ...AppFontStyle.SEMIBOLD_ANYBODY_16,
      color: AppColors.white,
    },
    confirmButton: {
      borderRadius: 6,
      overflow: "hidden",
    },
    confirmButtonInner: {
      paddingVertical: 12,
      paddingHorizontal: 24,
      alignItems: "center",
      justifyContent: "center",
    },
    confirmText: {
      ...AppFontStyle.SEMIBOLD_ANYBODY_16,
      color: AppColors.white,
    },
  },
  {
    [DEVICE_SIZES.XS]: {
      modalContainer: {
        width: "95%",
        padding: 16,
        gap: 16,
      },
      headerText: {
        ...AppFontStyle.BOLD_ANYBODY_18,
      },
      detailsContainer: {
        height: "auto",
        padding: 10,
        gap: 10,
      },
      labelText: {
        ...AppFontStyle.REGULAR_ANYBODY_12,
      },
      valueText: {
        ...AppFontStyle.SEMIBOLD_ANYBODY_14,
      },
      confirmText: {
        ...AppFontStyle.SEMIBOLD_ANYBODY_14,
      },
      makeChangesText: {
        ...AppFontStyle.SEMIBOLD_ANYBODY_14,
      },
    },
    [DEVICE_SIZES.SM]: {
      modalContainer: {
        width: "90%",
        padding: 20,
        gap: 18,
      },
      detailsContainer: {
        height: "auto",
      },
    },
    [DEVICE_SIZES.MD]: {
      modalContainer: {
        width: "70%",
      },
    },
    [DEVICE_SIZES.LG]: {
      modalContainer: {
        width: "50%",
      },
    },
  }
);
